// components/portal/PageCrop.tsx
'use client';

import React from 'react';

/**
 * A fixed box showing one region of a larger image, centred on a point of it.
 *
 * `span` is the share of the image's width visible across the box, so the same point reads at
 * the same magnification whatever the box is; the offset is clamped to the image so a point near
 * an edge never shows blank canvas. Pure arithmetic, exported so a test can pin it.
 */
export interface CropLayout { imageWidth: number; imageHeight: number; left: number; top: number }

export const cropLayout = (
  page: { width: number; height: number },
  centre: { x: number; y: number },
  box: { width: number; height: number },
  span: number,
): CropLayout => {
  const scale = box.width / (page.width * span);
  const imageWidth = page.width * scale;
  const imageHeight = page.height * scale;
  const clamp = (value: number, max: number) => Math.min(0, Math.max(value, max));
  return {
    imageWidth,
    imageHeight,
    left: clamp(box.width / 2 - centre.x * scale, box.width - imageWidth),
    top: clamp(box.height / 2 - centre.y * scale, box.height - imageHeight),
  };
};

export interface PageCropProps {
  src: string;
  pageWidth: number;
  pageHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
  span?: number;
  alt?: string;
  className?: string;
}

// The data attributes let the Table's cell panel rebuild the same crop, larger, without knowing
// anything about what the image is.
export const PageCrop = React.memo(function PageCrop({ src, pageWidth, pageHeight, x, y, width, height, span = 0.28, alt = '', className }: PageCropProps) {
  const layout = cropLayout({ width: pageWidth, height: pageHeight }, { x, y }, { width, height }, span);
  return (
    <div className={`relative shrink-0 overflow-hidden rounded-md bg-[#2A2A2A] ${className ?? ''}`} style={{ width, height }}
      data-crop-src={src} data-crop-page={`${pageWidth},${pageHeight}`} data-crop-centre={`${x},${y}`}>
      <img src={src} alt={alt} draggable={false} className="absolute max-w-none select-none"
        style={{ width: layout.imageWidth, height: layout.imageHeight, left: layout.left, top: layout.top }} />
    </div>
  );
});
